import { useState, useEffect } from 'react';
import { X, Download, Share } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const InstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if iOS
    const ios = /iPhone|iPad|iPod/.test(navigator.userAgent);
    setIsIOS(ios);

    // Check if already installed
    const standalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone
      || document.referrer.includes('android-app://');
    setIsStandalone(standalone);

    // Check if user has dismissed before
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    
    // Show prompt if not iOS, not standalone, and not dismissed
    if (!standalone && !dismissed) {
      setTimeout(() => setShowPrompt(true), 3000);
    }

    // Listen for beforeinstallprompt event (Android)
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      if (!dismissed) {
        setShowPrompt(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handler);

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('PWA installed');
    }
    
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-install-dismissed', 'true');
  };

  // Don't show if already installed
  if (isStandalone || !showPrompt) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-4 md:w-96">
      <Card className="bg-gradient-to-br from-orange-600 to-red-600 border-none shadow-2xl">
        <div className="p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 p-2 rounded-lg">
                <Download className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg">Install Mertek App</h3>
                <p className="text-white/90 text-sm">Access faster & work offline</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleDismiss}
              className="text-white hover:bg-white/20 -mr-2 -mt-2"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {isIOS ? (
            // iOS Instructions
            <div className="bg-white/10 rounded-lg p-3 mb-3">
              <p className="text-white text-sm mb-2 font-medium">To install on iPhone/iPad:</p>
              <ol className="text-white/90 text-sm space-y-1 list-decimal list-inside">
                <li>Tap the <Share className="w-4 h-4 inline mx-1" /> Share button below</li>
                <li>Scroll and tap "Add to Home Screen"</li>
                <li>Tap "Add" to install</li>
              </ol>
            </div>
          ) : (
            // Android Install Button
            <Button
              onClick={handleInstall}
              className="w-full bg-white text-orange-600 hover:bg-white/90 font-semibold"
            >
              <Download className="w-4 h-4 mr-2" />
              Install App
            </Button>
          )}

          <p className="text-white/70 text-xs mt-2 text-center">
            Works like a native app • No app store needed
          </p>
        </div>
      </Card>
    </div>
  );
};

export default InstallPrompt;
